/**
 * CompetencyList Component
 *
 * Displays and allows editing of competency weights for an assessment package.
 * Features:
 * - Table layout: name, category, scoringType, weight
 * - Editable weight inputs (draft only)
 * - Weight sum indicator (green=valid, red=invalid)
 * - Collapsible behavioral anchors
 * - Save button with validation
 */

import { useState, useEffect, useCallback } from 'react';
import type { CompetencyDefinition, AssessmentPackageResponse } from '../lib/types';

interface CompetencyListProps {
  package_: AssessmentPackageResponse;
  isDraft: boolean;
  onWeightsUpdate: (weights: Record<string, number>, version: number) => Promise<void>;
  isUpdating: boolean;
  updateError: string | null;
}

// Tolerance for weight sum validation (matches backend)
const WEIGHT_SUM_TOLERANCE = 0.001;

// Decimal places for display
const WEIGHT_PRECISION = 4;

function roundWeight(weight: number): number {
  const factor = Math.pow(10, WEIGHT_PRECISION);
  return Math.round(weight * factor) / factor;
}

function formatWeight(weight: number): string {
  return (weight * 100).toFixed(2) + '%';
}

export function CompetencyList({
  package_,
  isDraft,
  onWeightsUpdate,
  isUpdating,
  updateError,
}: CompetencyListProps) {
  // Local state for editing weights
  const [editedWeights, setEditedWeights] = useState<Record<string, number>>(() => {
    const weights: Record<string, number> = {};
    package_.competencies.forEach((c) => {
      weights[c.id] = c.weight;
    });
    return weights;
  });

  // Track which behavioral anchors are expanded
  const [expandedCompetencies, setExpandedCompetencies] = useState<Set<string>>(new Set());

  // Reset local state when package changes
  useEffect(() => {
    const weights: Record<string, number> = {};
    package_.competencies.forEach((c) => {
      weights[c.id] = c.weight;
    });
    setEditedWeights(weights);
  }, [package_]);

  // Calculate weight sum
  const weightSum = Object.values(editedWeights).reduce((sum, w) => sum + roundWeight(w), 0);
  const isValidSum = Math.abs(weightSum - 1.0) <= WEIGHT_SUM_TOLERANCE;

  // Check if weights have changed
  const hasChanges = package_.competencies.some(
    (c) => roundWeight(editedWeights[c.id] ?? c.weight) !== roundWeight(c.weight)
  );

  const handleWeightChange = useCallback((competencyId: string, value: string) => {
    const numValue = parseFloat(value) / 100; // Convert from percentage
    if (!isNaN(numValue)) {
      setEditedWeights((prev) => ({
        ...prev,
        [competencyId]: roundWeight(numValue),
      }));
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!isValidSum || !hasChanges || isUpdating) return;
    await onWeightsUpdate(editedWeights, package_.version);
  }, [editedWeights, package_.version, isValidSum, hasChanges, isUpdating, onWeightsUpdate]);

  const toggleExpanded = useCallback((competencyId: string) => {
    setExpandedCompetencies((prev) => {
      const next = new Set(prev);
      if (next.has(competencyId)) {
        next.delete(competencyId);
      } else {
        next.add(competencyId);
      }
      return next;
    });
  }, []);

  return (
    <div style={{ marginTop: 16 }}>
      {/* Header with weight sum indicator */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Competencies ({package_.competencies.length})
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Weight sum indicator */}
          <span
            style={{
              fontSize: 12,
              padding: '4px 8px',
              borderRadius: 4,
              background: isValidSum ? 'var(--semantic-success-light)' : 'var(--semantic-error-light)',
              color: isValidSum ? 'var(--semantic-success-text)' : 'var(--semantic-error-text)',
            }}
          >
            Sum: {formatWeight(weightSum)} {isValidSum ? '\u2713' : '\u2717'}
          </span>

          {/* Save button (only in draft mode) */}
          {isDraft && (
            <button
              onClick={handleSave}
              disabled={!isValidSum || !hasChanges || isUpdating}
              style={{
                padding: '6px 16px',
                borderRadius: 4,
                border: 'none',
                background:
                  isValidSum && hasChanges && !isUpdating
                    ? 'var(--accent-primary)'
                    : 'var(--bg-muted)',
                color:
                  isValidSum && hasChanges && !isUpdating
                    ? 'white'
                    : 'var(--text-secondary)',
                cursor: isValidSum && hasChanges && !isUpdating ? 'pointer' : 'not-allowed',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {isUpdating ? 'Saving...' : 'Save Weights'}
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {updateError && (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            background: 'var(--semantic-error-light)',
            color: 'var(--semantic-error-text)',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          {updateError}
        </div>
      )}

      {/* Competency table */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Name</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Category</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Type</th>
            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, width: 100 }}>
              Weight
            </th>
          </tr>
        </thead>
        <tbody>
          {package_.competencies.map((competency) => (
            <CompetencyRow
              key={competency.id}
              competency={competency}
              weight={editedWeights[competency.id] ?? competency.weight}
              isExpanded={expandedCompetencies.has(competency.id)}
              isDraft={isDraft}
              onWeightChange={(value) => handleWeightChange(competency.id, value)}
              onToggleExpand={() => toggleExpanded(competency.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface CompetencyRowProps {
  competency: CompetencyDefinition;
  weight: number;
  isExpanded: boolean;
  isDraft: boolean;
  onWeightChange: (value: string) => void;
  onToggleExpand: () => void;
}

function CompetencyRow({
  competency,
  weight,
  isExpanded,
  isDraft,
  onWeightChange,
  onToggleExpand,
}: CompetencyRowProps) {
  const hasBehavioralAnchors = competency.behavioralAnchors.length > 0;

  return (
    <>
      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        {/* Name with expand toggle */}
        <td style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            {hasBehavioralAnchors && (
              <button
                onClick={onToggleExpand}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginTop: 2,
                }}
                title={isExpanded ? 'Collapse' : 'Expand behavioral anchors'}
              >
                {isExpanded ? '\u25BC' : '\u25B6'}
              </button>
            )}
            <div>
              <div style={{ fontWeight: 500 }}>{competency.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {competency.description}
              </div>
            </div>
          </div>
        </td>

        {/* Category */}
        <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
          {competency.category || '-'}
        </td>

        {/* Scoring Type */}
        <td style={{ padding: '10px 12px' }}>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 500,
              textTransform: 'uppercase',
              background:
                competency.scoringType === 'rubric'
                  ? 'var(--semantic-info-light)'
                  : competency.scoringType === 'scale'
                    ? 'var(--semantic-warning-light)'
                    : 'var(--bg-muted)',
              color:
                competency.scoringType === 'rubric'
                  ? 'var(--semantic-info-text)'
                  : competency.scoringType === 'scale'
                    ? 'var(--semantic-warning-text)'
                    : 'var(--text-secondary)',
            }}
          >
            {competency.scoringType}
          </span>
        </td>

        {/* Weight */}
        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
          {isDraft ? (
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={(weight * 100).toFixed(1)}
              onChange={(e) => onWeightChange(e.target.value)}
              style={{
                width: 70,
                padding: '4px 8px',
                borderRadius: 4,
                border: '1px solid var(--border-default)',
                textAlign: 'right',
                fontSize: 13,
              }}
            />
          ) : (
            <span className="mono">{formatWeight(weight)}</span>
          )}
        </td>
      </tr>

      {/* Expanded behavioral anchors */}
      {isExpanded && hasBehavioralAnchors && (
        <tr>
          <td colSpan={4} style={{ padding: '0 12px 12px 36px' }}>
            <div
              style={{
                background: 'var(--bg-muted)',
                borderRadius: 4,
                padding: 12,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                Behavioral Anchors
              </div>
              {competency.behavioralAnchors.map((anchor, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 0',
                    borderBottom:
                      index < competency.behavioralAnchors.length - 1
                        ? '1px solid var(--border-subtle)'
                        : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        minWidth: 60,
                      }}
                    >
                      Level {anchor.level}
                    </span>
                    <span style={{ fontSize: 12 }}>{anchor.description}</span>
                  </div>
                  {anchor.indicators && anchor.indicators.length > 0 && (
                    <ul
                      style={{
                        margin: '4px 0 0 68px',
                        padding: 0,
                        listStyle: 'disc inside',
                        fontSize: 11,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {anchor.indicators.map((indicator, i) => (
                        <li key={i}>{indicator}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
